/**
 * Toolbar for the Slate email editor with all capabilities:
 * headings, bold/italic/underline/strike, lists, alignment, link, image, table, clear format.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Editor, Element as SlateElement, Node, NodeEntry, Range, Text, Transforms } from 'slate';
import type { Path } from 'slate';
import { useSlate, ReactEditor } from 'slate-react';
import { HistoryEditor } from 'slate-history';
import type { CustomElement, CustomText } from './slate-types';
import { slateToHtml, htmlToSlate } from './slate-html';
import { readClipboardForPaste, readClipboardForPlainPaste, writeHtmlAndPlainToClipboard } from './clipboard-helpers';
import { insertEmailImage, normalizeEditorSelection } from './slate-editor-utils';
import { cn } from '../../lib/utils';
import { buildThunderbirdFontSections, flattenFontSections, type FontFamilySection } from './thunderbirdFonts';

const ICON_SIZE = 16;
const iconProps = { width: ICON_SIZE, height: ICON_SIZE, strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };

const ToolbarIcons = {
  bold: (
    <svg {...iconProps} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
      <path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
    </svg>
  ),
  italic: (
    <svg {...iconProps} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <line x1="19" y1="4" x2="10" y2="4" />
      <line x1="14" y1="20" x2="5" y2="20" />
      <line x1="15" y1="4" x2="9" y2="20" />
    </svg>
  ),
  underline: (
    <svg {...iconProps} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path d="M6 3v7a6 6 0 0 0 12 0V3" />
      <line x1="4" y1="21" x2="20" y2="21" />
    </svg>
  ),
  strikethrough: (
    <svg {...iconProps} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path d="M16 4H9a3 3 0 0 0-2.83 4" />
      <path d="M14 12a4 4 0 0 1 0 8H6" />
      <line x1="4" y1="12" x2="20" y2="12" />
    </svg>
  ),
  highlight: (
    <svg {...iconProps} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  ),
  list: (
    <svg {...iconProps} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <line x1="8" y1="6" x2="21" y2="6" />
      <line x1="8" y1="12" x2="21" y2="12" />
      <line x1="8" y1="18" x2="21" y2="18" />
      <line x1="3" y1="6" x2="3.01" y2="6" />
      <line x1="3" y1="12" x2="3.01" y2="12" />
      <line x1="3" y1="18" x2="3.01" y2="18" />
    </svg>
  ),
  listOrdered: (
    <svg {...iconProps} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <line x1="10" y1="6" x2="21" y2="6" />
      <line x1="10" y1="12" x2="21" y2="12" />
      <line x1="10" y1="18" x2="21" y2="18" />
      <path d="M4 6h1v4" />
      <path d="M4 10h2" />
      <path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1" />
    </svg>
  ),
  alignLeft: (
    <svg {...iconProps} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <line x1="21" y1="6" x2="3" y2="6" />
      <line x1="15" y1="12" x2="3" y2="12" />
      <line x1="17" y1="18" x2="3" y2="18" />
    </svg>
  ),
  alignCenter: (
    <svg {...iconProps} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <line x1="21" y1="6" x2="3" y2="6" />
      <line x1="17" y1="12" x2="7" y2="12" />
      <line x1="19" y1="18" x2="5" y2="18" />
    </svg>
  ),
  alignRight: (
    <svg {...iconProps} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <line x1="21" y1="6" x2="3" y2="6" />
      <line x1="21" y1="12" x2="9" y2="12" />
      <line x1="21" y1="18" x2="7" y2="18" />
    </svg>
  ),
  alignJustify: (
    <svg {...iconProps} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <line x1="21" y1="6" x2="3" y2="6" />
      <line x1="21" y1="12" x2="3" y2="12" />
      <line x1="21" y1="18" x2="3" y2="18" />
    </svg>
  ),
  link: (
    <svg {...iconProps} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  ),
  image: (
    <svg {...iconProps} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <path d="M21 15l-5-5L5 21" />
    </svg>
  ),
  table: (
    <svg {...iconProps} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <rect x="3" y="3" width="18" height="18" rx="1" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <line x1="3" y1="15" x2="21" y2="15" />
      <line x1="9" y1="3" x2="9" y2="21" />
      <line x1="15" y1="3" x2="15" y2="21" />
    </svg>
  ),
  merge: (
    <svg {...iconProps} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path d="M8 3v18" />
      <path d="M16 3v18" />
      <path d="M8 12h8" />
    </svg>
  ),
  rowAbove: (
    <svg {...iconProps} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path d="M12 5v14" />
      <path d="M5 12h14" />
      <path d="M5 9V5h14v4" />
    </svg>
  ),
  rowBelow: (
    <svg {...iconProps} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path d="M12 5v14" />
      <path d="M5 12h14" />
      <path d="M5 15v4h14v-4" />
    </svg>
  ),
  /** Equal vertical dividers — equal column widths */
  columnsEqual: (
    <svg {...iconProps} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <rect x="3" y="4" width="18" height="16" rx="1" />
      <line x1="9" y1="4" x2="9" y2="20" />
      <line x1="15" y1="4" x2="15" y2="20" />
    </svg>
  ),
  /** Equal horizontal dividers — uniform row heights */
  rowsEqual: (
    <svg {...iconProps} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <rect x="3" y="4" width="18" height="16" rx="1" />
      <line x1="3" y1="10" x2="21" y2="10" />
      <line x1="3" y1="16" x2="21" y2="16" />
    </svg>
  ),
  clearFormat: (
    <svg {...iconProps} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path d="M4 7V4h16v3" />
      <path d="M5 20h6" />
      <path d="M13 4L9 20" />
      <path d="M18 15l-4-4" />
      <path d="M14 15l-4 4" />
    </svg>
  ),
  more: (
    <svg {...iconProps} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <circle cx="12" cy="6" r="1.5" />
      <circle cx="12" cy="12" r="1.5" />
      <circle cx="12" cy="18" r="1.5" />
    </svg>
  ),
  /** Open padlock — unlock quoted message below signature in reply/forward */
  unlockOpen: (
    <svg {...iconProps} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M13.5 10.5V6.75a4.5 4.5 0 119 0v3.75M6.75 12h12.75a2.25 2.25 0 012.25 2.25V19.5A2.25 2.25 0 0119.5 21.75h-15a2.25 2.25 0 01-2.25-2.25v-5.25A2.25 2.25 0 016.75 12z"
      />
    </svg>
  ),
  undo: (
    <svg {...iconProps} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path d="M3 10h10a5 5 0 0 1 5 5v0" />
      <path d="M3 10l4-4M3 10l4 4" />
    </svg>
  ),
  redo: (
    <svg {...iconProps} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path d="M21 10H11a5 5 0 0 0-5 5v0" />
      <path d="M21 10l-4-4M21 10l-4 4" />
    </svg>
  ),
  fontSizeDown: (
    <svg {...iconProps} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path d="M5 20L11 6l6 14" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
      <path d="M7 13h8" strokeWidth={2} strokeLinecap="round" />
      <path d="M15 7h6" strokeWidth={2} strokeLinecap="round" />
    </svg>
  ),
  fontSizeUp: (
    <svg {...iconProps} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path d="M5 20L11 6l6 14" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
      <path d="M7 13h8" strokeWidth={2} strokeLinecap="round" />
      <path d="M18 5v5M15.5 7.5h5" strokeWidth={2} strokeLinecap="round" />
    </svg>
  ),
  chevronDown: (
    <svg {...iconProps} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path d="M6 9l6 6 6-6" />
    </svg>
  ),
  cut: (
    <svg {...iconProps} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path d="M6 2L4 6v14a2 2 0 002 2h12a2 2 0 002-2V6l-2-4" />
      <path d="M16 10h4" />
      <path d="M15 14h5" />
      <path d="M9 2h6" />
      <path d="M8 6h8" />
    </svg>
  ),
  copy: (
    <svg {...iconProps} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
    </svg>
  ),
  paste: (
    <svg {...iconProps} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2" />
      <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
    </svg>
  ),
  type: (
    <svg {...iconProps} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <polyline points="4 7 4 4 20 4 20 7" />
      <line x1="9" y1="20" x2="15" y2="20" />
      <line x1="12" y1="4" x2="12" y2="20" />
    </svg>
  ),
} as const;

export interface SlateEmailToolbarConfig {
  showHeadings?: boolean;
  showFont?: boolean;
  showLink?: boolean;
  showImage?: boolean;
  showTable?: boolean;
  showLists?: boolean;
  showAlignment?: boolean;
  showColors?: boolean;
  onError?: (message: string) => void;
  /** Reply/forward compose: unlock quoted message + header for editing (shown in toolbar). */
  unlockQuotedBelow?: {
    visible: boolean;
    onUnlock: () => void;
    /** Tooltip and aria-label; default explains quoted + header editing */
    title?: string;
    ariaLabel?: string;
  };
}

const DEFAULT_CONFIG: SlateEmailToolbarConfig = {
  showHeadings: true,
  showFont: true,
  showLists: true,
  showAlignment: true,
  showColors: true,
  showLink: true,
  showImage: true,
  showTable: true,
};

const FONT_SIZES_PX = [10, 12, 14, 16, 18, 20, 24, 28, 32];
const DEFAULT_FONT_SIZE_PX = 14;
const DEFAULT_HIGHLIGHT_COLOR = '#ffff00';
const FONT_MENU_SEPARATOR = '────────';

/** Stable fallback before client-side font detection runs. */
const FALLBACK_FONT_SECTIONS: FontFamilySection[] = [
  {
    kind: 'options',
    options: [
      { value: '', label: 'Variable Width' },
      { value: 'monospace', label: 'Fixed Width' },
    ],
  },
  { kind: 'separator' },
  {
    kind: 'options',
    options: [
      { value: 'Helvetica, Arial, sans-serif', label: 'Helvetica, Arial' },
      { value: 'Times, "Times New Roman", serif', label: 'Times' },
      { value: 'Courier, "Courier New", monospace', label: 'Courier' },
    ],
  },
];

/** Mark keys that can have string values (excludes 'text'). */
type StringMarkKey = Exclude<keyof CustomText, 'text'>;

function hexForColorInput(color: string | undefined, fallback: string): string {
  if (!color?.trim()) return fallback;
  if (/^#[0-9a-f]{3}$/i.test(color) || /^#[0-9a-f]{6}$/i.test(color)) return color;
  if (typeof document === 'undefined') return fallback;
  const el = document.createElement('div');
  el.style.color = '';
  el.style.backgroundColor = color;
  const normalized = el.style.backgroundColor;
  if (/^#[0-9a-f]{3}$/i.test(normalized) || /^#[0-9a-f]{6}$/i.test(normalized)) return normalized;
  el.style.color = color;
  const textColor = el.style.color;
  if (/^#[0-9a-f]{3}$/i.test(textColor) || /^#[0-9a-f]{6}$/i.test(textColor)) return textColor;
  return fallback;
}

function getMarkValue(editor: Editor, key: StringMarkKey): string | undefined {
  const marks = Editor.marks(editor) as Partial<CustomText> | null;
  if (marks) {
    const v = marks[key];
    if (v !== true && v !== false && typeof v === 'string' && v.length > 0) return v;
  }
  // When the subject field (or elsewhere) is focused, selection is null — still show
  // the document's starting marks so compose defaults remain visible in the toolbar.
  try {
    const start = Editor.start(editor, []);
    const [node] = Editor.node(editor, start.path);
    if (Text.isText(node)) {
      const leaf = node as CustomText;
      const v = leaf[key];
      if (typeof v === 'string' && v.length > 0) return v;
    }
  } catch {
    // empty doc
  }
  return undefined;
}

function getMarkValueAtSelection(editor: Editor, key: StringMarkKey): string | undefined {
  const pending = getMarkValue(editor, key);
  if (pending) return pending;
  const { selection } = editor;
  if (!selection) return undefined;
  try {
    const values = Array.from(
      Editor.nodes(editor, { at: selection, match: Text.isText }),
    )
      .map(([node]) => (node as CustomText)[key])
      .filter((v): v is string => typeof v === 'string' && v.length > 0);
    if (values.length === 0) return undefined;
    const first = values[0];
    return values.every((v) => v === first) ? first : first;
  } catch {
    return undefined;
  }
}

function hasHighlightAtSelection(editor: Editor): boolean {
  return Boolean(getMarkValueAtSelection(editor, 'backgroundColor'));
}

function applyHighlight(editor: Editor, color: string = DEFAULT_HIGHLIGHT_COLOR): void {
  const current = getMarkValueAtSelection(editor, 'backgroundColor');
  if (
    current &&
    hexForColorInput(current, '').toLowerCase() === hexForColorInput(color, '').toLowerCase()
  ) {
    applyMarkToSelection(editor, 'backgroundColor', undefined);
    return;
  }
  applyMarkToSelection(editor, 'backgroundColor', color);
}

function setMarkValue(editor: Editor, key: StringMarkKey, value: string) {
  applyMarkToSelection(editor, key, value || undefined);
}

function applyMarkToSelection(
  editor: Editor,
  key: StringMarkKey,
  value: string | boolean | undefined,
): void {
  const { selection } = editor;
  if (!selection) return;

  const markValue = value === '' || value === false ? undefined : value;

  if (Range.isExpanded(selection)) {
    Transforms.setNodes(
      editor,
      { [key]: markValue } as Partial<CustomText>,
      { match: Text.isText, split: true, at: selection },
    );
    return;
  }

  if (markValue === undefined) Editor.removeMark(editor, key);
  else Editor.addMark(editor, key, markValue);
}

function runToolbarMutation(editor: Editor, onContentChange: (() => void) | undefined, fn: () => void) {
  fn();
  queueMicrotask(() => onContentChange?.());
}

function currentFontSizePx(editor: Editor): number {
  const v = getMarkValue(editor, 'fontSize');
  if (!v) return DEFAULT_FONT_SIZE_PX;
  const n = parseInt(v.replace(/px$/i, ''), 10);
  return Number.isNaN(n) ? DEFAULT_FONT_SIZE_PX : n;
}

function increaseFontSize(editor: Editor, onContentChange?: () => void) {
  const current = currentFontSizePx(editor);
  const idx = FONT_SIZES_PX.indexOf(current);
  const next = idx < 0 ? (current < DEFAULT_FONT_SIZE_PX ? DEFAULT_FONT_SIZE_PX : Math.min(32, current + 2)) : FONT_SIZES_PX[Math.min(idx + 1, FONT_SIZES_PX.length - 1)];
  runToolbarMutation(editor, onContentChange, () => setMarkValue(editor, 'fontSize', `${next}px`));
}

function decreaseFontSize(editor: Editor, onContentChange?: () => void) {
  const current = currentFontSizePx(editor);
  const idx = FONT_SIZES_PX.indexOf(current);
  const next = idx <= 0 ? (current <= 10 ? 10 : Math.max(10, current - 2)) : FONT_SIZES_PX[idx - 1];
  runToolbarMutation(editor, onContentChange, () => setMarkValue(editor, 'fontSize', `${next}px`));
}

export interface SlateEmailToolbarProps {
  config?: SlateEmailToolbarConfig;
  onInsertImage?: () => void;
  /** Called after toolbar actions that mutate content outside the normal typing flow (e.g. file embed). */
  onContentChange?: () => void;
  className?: string;
  colors?: Record<string, string>;
}

const LIST_TYPES = ['numbered-list', 'bulleted-list'];
/** Thunderbird Paragraph Style block types (formatblock equivalents). */
const PARAGRAPH_STYLE_TYPES = [
  'body-text',
  'paragraph',
  'heading-one',
  'heading-two',
  'heading-three',
  'heading-four',
  'heading-five',
  'heading-six',
  'address',
  'preformat',
] as const;

type ParagraphStyleValue =
  | 'body'
  | 'p'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'address'
  | 'pre';

const PARAGRAPH_STYLE_TO_BLOCK: Record<ParagraphStyleValue, (typeof PARAGRAPH_STYLE_TYPES)[number]> = {
  body: 'body-text',
  p: 'paragraph',
  h1: 'heading-one',
  h2: 'heading-two',
  h3: 'heading-three',
  h4: 'heading-four',
  h5: 'heading-five',
  h6: 'heading-six',
  address: 'address',
  pre: 'preformat',
};

function blockTypeToParagraphStyle(type: string): ParagraphStyleValue | null {
  if (type === 'body-text') return 'body';
  if (type === 'heading-one') return 'h1';
  if (type === 'heading-two') return 'h2';
  if (type === 'heading-three') return 'h3';
  if (type === 'heading-four') return 'h4';
  if (type === 'heading-five') return 'h5';
  if (type === 'heading-six') return 'h6';
  if (type === 'address') return 'address';
  if (type === 'preformat') return 'pre';
  if (type === 'paragraph') return 'p';
  return null;
}

function getActiveParagraphStyle(editor: Editor): ParagraphStyleValue {
  if (isBlockActive(editor, 'body-text')) return 'body';
  if (isBlockActive(editor, 'heading-one')) return 'h1';
  if (isBlockActive(editor, 'heading-two')) return 'h2';
  if (isBlockActive(editor, 'heading-three')) return 'h3';
  if (isBlockActive(editor, 'heading-four')) return 'h4';
  if (isBlockActive(editor, 'heading-five')) return 'h5';
  if (isBlockActive(editor, 'heading-six')) return 'h6';
  if (isBlockActive(editor, 'address')) return 'address';
  if (isBlockActive(editor, 'preformat')) return 'pre';
  if (isBlockActive(editor, 'paragraph')) return 'p';
  // No selection (e.g. subject focused): read the first block so compose defaults show.
  const first = editor.children[0];
  if (SlateElement.isElement(first)) {
    const style = blockTypeToParagraphStyle((first as CustomElement).type);
    if (style) return style;
  }
  return 'p';
}

type MarkKey = Exclude<keyof CustomText, 'text'>;

function isMarkActive(editor: Editor, mark: MarkKey): boolean {
  try {
    const marks = Editor.marks(editor);
    return marks ? (marks[mark] === true) : false;
  } catch {
    return false;
  }
}

function toggleMark(editor: Editor, mark: MarkKey) {
  const active = isMarkActive(editor, mark);
  applyMarkToSelection(editor, mark, active ? undefined : true);
}

function isBlockActive(editor: Editor, type: string): boolean {
  try {
    if (!editor.selection) return false;
    Editor.node(editor, editor.selection.anchor.path);
    const matches = Array.from(Editor.nodes(editor, {
      match: (n) => SlateElement.isElement(n) && (n as CustomElement).type === type,
    }));
    return matches.length > 0;
  } catch {
    return false;
  }
}

function isAlignActive(editor: Editor, align: string): boolean {
  try {
    if (!editor.selection) return false;
    Editor.node(editor, editor.selection.anchor.path);
    const matches = Array.from(Editor.nodes(editor, {
      match: (n) => SlateElement.isElement(n) && (n as { align?: string }).align === align,
    }));
    return matches.length > 0;
  } catch {
    return false;
  }
}

function toggleBlock(editor: Editor, type: CustomElement['type']) {
  const isList = LIST_TYPES.includes(type);
  const isParagraphStyle = (PARAGRAPH_STYLE_TYPES as readonly string[]).includes(type);
  Transforms.unwrapNodes(editor, {
    match: (n) => SlateElement.isElement(n) && LIST_TYPES.includes((n as CustomElement).type),
    split: true,
  });
  if (isParagraphStyle) {
    Transforms.setNodes(editor, { type } as Partial<CustomElement>);
    // Headings use Gecko font-size; drop inline px so Heading 1–6 look like Thunderbird.
    if (type.startsWith('heading-')) {
      Transforms.unsetNodes(editor, 'fontSize', { match: Text.isText });
    }
  } else if (isList) {
    const block = { type: type as 'bulleted-list' | 'numbered-list', children: [] };
    Transforms.wrapNodes(editor, block);
    Transforms.wrapNodes(editor, { type: 'list-item', children: [] } as CustomElement);
  } else {
    Transforms.setNodes(editor, { type } as Partial<CustomElement>);
  }
}

function setAlign(editor: Editor, align: 'left' | 'center' | 'right' | 'justify') {
  Transforms.setNodes(editor, { align } as Partial<CustomElement>, {
    match: (n) => SlateElement.isElement(n) && Editor.isBlock(editor, n),
  });
}

function insertTable(editor: Editor, rows: number, cols: number) {
  const cellParagraph: CustomElement = { type: 'paragraph', children: [{ text: '' }] };
  const table: CustomElement = {
    type: 'table',
    children: Array.from({ length: rows }, () => ({
      type: 'table-row',
      children: Array.from({ length: cols }, () => ({
        type: 'table-cell',
        children: [{ ...cellParagraph, children: [...cellParagraph.children] }],
      })),
    })),
  };
  Transforms.insertNodes(editor, table);
}

/** Table size options: rows × columns. */
const TABLE_SIZE_OPTIONS: { rows: number; cols: number; label: string }[] = [
  { rows: 2, cols: 2, label: '2×2' },
  { rows: 2, cols: 3, label: '2×3' },
  { rows: 2, cols: 4, label: '2×4' },
  { rows: 3, cols: 2, label: '3×2' },
  { rows: 3, cols: 3, label: '3×3' },
  { rows: 3, cols: 4, label: '3×4' },
  { rows: 4, cols: 2, label: '4×2' },
  { rows: 4, cols: 3, label: '4×3' },
  { rows: 4, cols: 4, label: '4×4' },
  { rows: 4, cols: 5, label: '4×5' },
  { rows: 5, cols: 4, label: '5×4' },
  { rows: 5, cols: 5, label: '5×5' },
];

/** Get selected table cells that are in the same row. Returns entries sorted by cell index, or empty if < 2. */
function getSelectedCellsInSameRow(editor: Editor): NodeEntry<CustomElement>[] {
  const { selection } = editor;
  if (!selection) return [];
  const entries = Array.from(
    Editor.nodes(editor, {
      at: selection,
      match: (n) => SlateElement.isElement(n) && (n as CustomElement).type === 'table-cell',
    })
  ) as NodeEntry<CustomElement>[];
  if (entries.length < 2) return [];
  const byRow = new Map<string, NodeEntry<CustomElement>[]>();
  for (const entry of entries) {
    const path = entry[1];
    if (path.length < 3) continue;
    const key = `${path[0]}-${path[1]}`;
    if (!byRow.has(key)) byRow.set(key, []);
    byRow.get(key)!.push(entry);
  }
  const rowArrays = Array.from(byRow.values());
  for (let i = 0; i < rowArrays.length; i++) {
    const rowEntries = rowArrays[i];
    if (rowEntries.length >= 2) {
      rowEntries.sort((a: NodeEntry<CustomElement>, b: NodeEntry<CustomElement>) => a[1][a[1].length - 1] - b[1][b[1].length - 1]);
      return rowEntries;
    }
  }
  return [];
}

function canMergeTableColumns(editor: Editor): boolean {
  return getSelectedCellsInSameRow(editor).length >= 2;
}

/** If selection is inside a table cell, return table path, row index, and cell count for that row. */
function getTableRowContext(editor: Editor): { tablePath: Path; rowIndex: number; cellCount: number } | null {
  const { selection } = editor;
  if (!selection) return null;
  const cellEntry = Editor.above(editor, {
    match: (n) => SlateElement.isElement(n) && (n as CustomElement).type === 'table-cell',
  });
  if (!cellEntry) return null;
  const cellPath = cellEntry[1];
  if (cellPath.length < 3) return null;
  const tablePath = cellPath.slice(0, -2) as Path;
  const rowIndex = cellPath[cellPath.length - 2] as number;
  const rowEntry = Editor.node(editor, cellPath.slice(0, -1));
  const rowNode = rowEntry[0];
  if (!SlateElement.isElement(rowNode) || (rowNode as CustomElement).type !== 'table-row') return null;
  const cellCount = (rowNode as { children: unknown[] }).children.length;
  return { tablePath, rowIndex, cellCount };
}

const MIN_UNIFORM_ROW_HEIGHT_PX = 20;
const MAX_UNIFORM_ROW_HEIGHT_PX = 400;

/** Selection inside any table cell → table path. */
function getTableContext(editor: Editor): { tablePath: Path } | null {
  const { selection } = editor;
  if (!selection) return null;
  const cellEntry = Editor.above(editor, {
    match: (n) => SlateElement.isElement(n) && (n as CustomElement).type === 'table-cell',
  });
  if (!cellEntry) return null;
  const cellPath = cellEntry[1];
  if (cellPath.length < 3) return null;
  const tablePath = cellPath.slice(0, -2) as Path;
  return { tablePath };
}

/** Set every cell in the table to the same percentage width (equal columns). */
function equalizeTableColumnWidths(editor: Editor) {
  const ctx = getTableContext(editor);
  if (!ctx) return;
  const { tablePath } = ctx;
  const table = Node.get(editor, tablePath);
  if (!SlateElement.isElement(table) || (table as CustomElement).type !== 'table') return;
  const rows = (table as CustomElement).children as CustomElement[];
  if (!rows.length) return;
  const firstRow = rows[0];
  if (!SlateElement.isElement(firstRow) || firstRow.type !== 'table-row') return;
  const columnCount = firstRow.children.length;
  if (columnCount < 1) return;
  const pct = (100 / columnCount).toFixed(4);
  const widthStr = `${pct}%`;
  Editor.withoutNormalizing(editor, () => {
    for (let r = 0; r < rows.length; r++) {
      const rowPath = [...tablePath, r];
      const row = Node.get(editor, rowPath) as CustomElement;
      if (!SlateElement.isElement(row) || row.type !== 'table-row') continue;
      const cells = row.children as CustomElement[];
      for (let c = 0; c < columnCount && c < cells.length; c++) {
        Transforms.setNodes(editor, { width: widthStr } as Partial<CustomElement>, { at: [...rowPath, c] });
      }
    }
  });
}

/** Apply the same row height to every row in the current table. */
function applyUniformTableRowHeight(editor: Editor, heightPx: number) {
  const ctx = getTableContext(editor);
  if (!ctx) return;
  const { tablePath } = ctx;
  const table = Node.get(editor, tablePath);
  if (!SlateElement.isElement(table) || (table as CustomElement).type !== 'table') return;
  const rows = (table as CustomElement).children as CustomElement[];
  const h = Math.max(MIN_UNIFORM_ROW_HEIGHT_PX, Math.min(MAX_UNIFORM_ROW_HEIGHT_PX, heightPx));
  const heightStr = `${Math.round(h)}px`;
  Editor.withoutNormalizing(editor, () => {
    for (let r = 0; r < rows.length; r++) {
      Transforms.setNodes(editor, { height: heightStr } as Partial<CustomElement>, { at: [...tablePath, r] });
    }
  });
}

function insertTableRow(editor: Editor, position: 'above' | 'below') {
  const ctx = getTableRowContext(editor);
  if (!ctx) return;
  const { tablePath, rowIndex, cellCount } = ctx;
  const insertIndex = position === 'above' ? rowIndex : rowIndex + 1;
  const newRow: CustomElement = {
    type: 'table-row',
    children: Array.from({ length: cellCount }, () => ({
      type: 'table-cell',
      children: [{ type: 'paragraph', children: [{ text: '' }] }],
    })),
  };
  Transforms.insertNodes(editor, newRow, { at: [...tablePath, insertIndex] });
  try {
    const firstCellPath = [...tablePath, insertIndex, 0];
    const start = Editor.start(editor, firstCellPath);
    Transforms.select(editor, start);
  } catch {
    // selection may already be valid
  }
}

function mergeTableColumns(editor: Editor) {
  const entries = getSelectedCellsInSameRow(editor);
  if (entries.length < 2) return;
  const paths = entries.map((e) => e[1]);
  const firstPath = paths[0];
  let totalColSpan = 0;
  const textParts: string[] = [];
  for (const [node, path] of entries) {
    totalColSpan += (node as { colSpan?: number }).colSpan ?? 1;
    textParts.push(Editor.string(editor, path));
  }
  const mergedText = textParts.join(' ').trim() || '';
  const firstCellChildren = [
    { type: 'paragraph' as const, children: [{ text: mergedText }] },
  ];
  Editor.withoutNormalizing(editor, () => {
    Transforms.setNodes(
      editor,
      { colSpan: totalColSpan, children: firstCellChildren } as Partial<CustomElement>,
      { at: firstPath }
    );
    for (let i = paths.length - 1; i >= 1; i--) {
      Transforms.removeNodes(editor, { at: paths[i] });
    }
  });
  try {
    const start = Editor.start(editor, firstPath);
    Transforms.select(editor, start);
  } catch {
    // selection may already be valid
  }
}

function unwrapLink(editor: Editor) {
  Transforms.unwrapNodes(editor, { match: (n) => SlateElement.isElement(n) && (n as CustomElement).type === 'link' });
}

function wrapLink(editor: Editor, url: string) {
  if (isLinkActive(editor)) unwrapLink(editor);
  const { selection } = editor;
  const isCollapsed = selection && Range.isCollapsed(selection);
  const link = { type: 'link' as const, url, children: isCollapsed ? [{ text: url }] : [] };
  if (isCollapsed) {
    Transforms.insertNodes(editor, link);
  } else {
    Transforms.wrapNodes(editor, link, { split: true });
  }
}

function isLinkActive(editor: Editor): boolean {
  try {
    if (!editor.selection) return false;
    Editor.node(editor, editor.selection.anchor.path);
    const matches = Array.from(Editor.nodes(editor, {
      match: (n) => SlateElement.isElement(n) && (n as CustomElement).type === 'link',
    }));
    return matches.length > 0;
  } catch {
    return false;
  }
}

/** Returns true if the range can be safely used with Transforms.select (avoids void nodes and invalid paths). */
function isRangeSafeForSelect(editor: Editor, range: Range): boolean {
  try {
    if (!range.anchor.path.length || !range.focus.path.length) return false;
    const anchorNode = Editor.node(editor, range.anchor.path)[0];
    const focusNode = Editor.node(editor, range.focus.path)[0];
    if (SlateElement.isElement(anchorNode) && (anchorNode as CustomElement).type === 'image') return false;
    if (SlateElement.isElement(focusNode) && (focusNode as CustomElement).type === 'image') return false;
    Editor.start(editor, range.anchor.path);
    Editor.start(editor, range.focus.path);
    return true;
  } catch {
    return false;
  }
}

function safeRestoreSelection(editor: Editor, saved: Range | null): void {
  if (!saved) return;
  try {
    if (!isRangeSafeForSelect(editor, saved)) return;
    Transforms.select(editor, saved);
  } catch {
    // ignore invalid selection restore
  }
}

function clearFormat(editor: Editor) {
  const { selection } = editor;
  if (!selection) return;
  const markKeys: StringMarkKey[] = [
    'bold',
    'italic',
    'underline',
    'strikethrough',
    'color',
    'backgroundColor',
    'fontFamily',
    'fontSize',
  ];
  if (Range.isExpanded(selection)) {
    for (const key of markKeys) {
      Transforms.unsetNodes(editor, key, { match: Text.isText, split: true, at: selection });
    }
  } else {
    for (const key of markKeys) Editor.removeMark(editor, key);
  }
  Transforms.setNodes(editor, { type: 'paragraph', align: undefined } as Partial<CustomElement>, {
    match: (n) => SlateElement.isElement(n) && Editor.isBlock(editor, n),
  });
}

function ensureEditorSelection(editor: Editor) {
  if (editor.selection) {
    normalizeEditorSelection(editor);
    return;
  }
  const end = Editor.end(editor, []);
  Transforms.select(editor, end);
}

const SlateEmailToolbar: React.FC<SlateEmailToolbarProps> = ({
  config = {},
  onInsertImage,
  onContentChange,
  className,
  colors = {},
}) => {
  const editor = useSlate();
  const opts = { ...DEFAULT_CONFIG, ...config };
  const [linkUrl, setLinkUrl] = useState('');
  const [linkOpen, setLinkOpen] = useState(false);
  const [tableDropdownOpen, setTableDropdownOpen] = useState(false);
  const [alignDropdownOpen, setAlignDropdownOpen] = useState(false);
  const [moreDropdownOpen, setMoreDropdownOpen] = useState(false);
  const [listDropdownOpen, setListDropdownOpen] = useState(false);
  const [insertDropdownOpen, setInsertDropdownOpen] = useState(false);
  const [editDropdownOpen, setEditDropdownOpen] = useState(false);
  const [fontSections, setFontSections] = useState<FontFamilySection[]>(FALLBACK_FONT_SECTIONS);
  const colorPickerSelectionRef = useRef<Range | null>(null);
  const colorInputRef = useRef<HTMLInputElement>(null);
  const highlightInputRef = useRef<HTMLInputElement>(null);
  const linkSelectionRef = useRef<Range | null>(null);
  const imageSelectionRef = useRef<Range | null>(null);
  const imageFileInputRef = useRef<HTMLInputElement>(null);
  const editSelectionRef = useRef<Range | null>(null);

  useEffect(() => {
    setFontSections(buildThunderbirdFontSections());
  }, []);

  const currentFontFamily = getMarkValue(editor, 'fontFamily') ?? '';
  const showCustomFontOption =
    currentFontFamily !== '' &&
    !flattenFontSections(fontSections).some((f) => f.value === currentFontFamily);

  const saveSelectionForEdit = useCallback(() => {
    const sel = editor.selection;
    if (!sel || !isRangeSafeForSelect(editor, sel)) {
      editSelectionRef.current = null;
      return;
    }
    editSelectionRef.current = {
      anchor: { path: [...sel.anchor.path], offset: sel.anchor.offset },
      focus: { path: [...sel.focus.path], offset: sel.focus.offset },
    };
  }, [editor, editor.selection]);

  const saveSelectionForLink = useCallback(() => {
    const sel = editor.selection;
    if (!sel || !isRangeSafeForSelect(editor, sel)) {
      linkSelectionRef.current = null;
      return;
    }
    linkSelectionRef.current = {
      anchor: { path: [...sel.anchor.path], offset: sel.anchor.offset },
      focus: { path: [...sel.focus.path], offset: sel.focus.offset },
    };
  }, [editor, editor.selection]);

  const handleLink = useCallback(() => {
    if (isLinkActive(editor)) {
      unwrapLink(editor);
      setLinkOpen(false);
      return;
    }
    const { selection } = editor;
    if (selection && !Range.isCollapsed(selection)) {
      const [node] = Editor.node(editor, selection);
      if (SlateElement.isElement(node) && node.type === 'link') {
        setLinkUrl((node as { url: string }).url);
      }
    }
    saveSelectionForLink();
    setLinkOpen((o) => !o);
  }, [editor, saveSelectionForLink]);

  const applyLink = useCallback(() => {
    const raw = linkUrl.trim();
    if (!raw) return;
    const lower = raw.toLowerCase();
    if (lower.startsWith('javascript:') || lower.startsWith('data:') || lower.startsWith('vbscript:')) {
      opts.onError?.('This link type is not allowed for security reasons.');
      return;
    }
    if (!/^(https?:\/\/|mailto:)/.test(lower)) {
      opts.onError?.('Link must start with http://, https://, or mailto:');
      return;
    }
    const saved = linkSelectionRef.current;
    linkSelectionRef.current = null;
    if (saved) safeRestoreSelection(editor, saved);
    wrapLink(editor, raw);
    setLinkUrl('');
    setLinkOpen(false);
    ReactEditor.focus(editor as ReactEditor);
  }, [editor, linkUrl, opts]);

  const c = { text: colors.text ?? '#374151', textMuted: colors.textMuted ?? '#6b7280', border: colors.border ?? '#e5e7eb' };

  const saveSelectionForColorPicker = useCallback(() => {
    const sel = editor.selection;
    if (!sel || !isRangeSafeForSelect(editor, sel)) {
      colorPickerSelectionRef.current = null;
      return;
    }
    colorPickerSelectionRef.current = {
      anchor: { path: [...sel.anchor.path], offset: sel.anchor.offset },
      focus: { path: [...sel.focus.path], offset: sel.focus.offset },
    };
  }, [editor, editor.selection]);

  const handleToolbarMouseDown = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('select')) return;
    if (target.closest('input[type="color"]')) {
      saveSelectionForColorPicker();
      return;
    }
    if (target.closest('[data-toolbar-link-ui]')) return;
    if (target.closest('[data-toolbar-image-ui]')) return;
    e.preventDefault();
  }, [saveSelectionForColorPicker]);

  const handleColorInputMouseDown = useCallback((e: React.MouseEvent<HTMLInputElement>) => {
    e.preventDefault();
    saveSelectionForColorPicker();
    setTimeout(() => colorInputRef.current?.click(), 0);
  }, [saveSelectionForColorPicker]);

  const handleHighlightInputMouseDown = useCallback((e: React.MouseEvent<HTMLInputElement>) => {
    e.preventDefault();
    saveSelectionForColorPicker();
    setTimeout(() => highlightInputRef.current?.click(), 0);
  }, [saveSelectionForColorPicker]);

  useEffect(() => {
    if (!alignDropdownOpen && !tableDropdownOpen && !moreDropdownOpen && !insertDropdownOpen && !listDropdownOpen && !editDropdownOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setAlignDropdownOpen(false);
        setTableDropdownOpen(false);
        setMoreDropdownOpen(false);
        setInsertDropdownOpen(false);
        setListDropdownOpen(false);
        setEditDropdownOpen(false);
        ReactEditor.focus(editor as ReactEditor);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [alignDropdownOpen, tableDropdownOpen, moreDropdownOpen, insertDropdownOpen, listDropdownOpen, editDropdownOpen, editor]);

  const handleCut = useCallback(async () => {
    const sel = editor.selection;
    const saved = editSelectionRef.current;
    const rangeToUse = (sel && !Range.isCollapsed(sel) ? sel : saved) ?? null;
    if (!rangeToUse || Range.isCollapsed(rangeToUse)) return;
    safeRestoreSelection(editor, rangeToUse);
    const fragment = editor.getFragment();
    if (!fragment.length) return;
    const html = slateToHtml(fragment);
    const plain = fragment.map((n) => Node.string(n)).join('\n');
    try {
      await writeHtmlAndPlainToClipboard(html, plain);
      Editor.deleteFragment(editor);
      editSelectionRef.current = null;
      setEditDropdownOpen(false);
      ReactEditor.focus(editor as ReactEditor);
    } catch {
      opts.onError?.('Cut failed. Try using Ctrl+X.');
    }
  }, [editor, opts]);

  const handleCopy = useCallback(async () => {
    const sel = editor.selection;
    const saved = editSelectionRef.current;
    const rangeToUse = (sel && !Range.isCollapsed(sel) ? sel : saved) ?? null;
    if (!rangeToUse || Range.isCollapsed(rangeToUse)) return;
    safeRestoreSelection(editor, rangeToUse);
    const fragment = editor.getFragment();
    if (!fragment.length) return;
    const html = slateToHtml(fragment);
    const plain = fragment.map((n) => Node.string(n)).join('\n');
    try {
      await writeHtmlAndPlainToClipboard(html, plain);
      setEditDropdownOpen(false);
      ReactEditor.focus(editor as ReactEditor);
    } catch {
      opts.onError?.('Copy failed. Try using Ctrl+C.');
    }
  }, [editor, opts]);

  const handlePaste = useCallback(async () => {
    const saved = editSelectionRef.current;
    ReactEditor.focus(editor as ReactEditor);
    const result = await readClipboardForPaste();
    if (result.status === 'denied') {
      opts.onError?.('Paste failed. Try using Ctrl+V.');
      return;
    }
    if (result.status === 'empty') {
      setEditDropdownOpen(false);
      ReactEditor.focus(editor as ReactEditor);
      return;
    }
    const html = result.html;
    const plain = result.plain;
    const toInsert = html ? htmlToSlate(html) : plain ? [{ type: 'paragraph' as const, children: [{ text: plain }] }] : null;
    if (toInsert?.length) {
      if (saved) safeRestoreSelection(editor, saved);
      Transforms.insertFragment(editor, toInsert);
    }
    setEditDropdownOpen(false);
    ReactEditor.focus(editor as ReactEditor);
  }, [editor, opts]);

  const handlePasteWithoutFormatting = useCallback(async () => {
    const saved = editSelectionRef.current;
    ReactEditor.focus(editor as ReactEditor);
    const result = await readClipboardForPlainPaste();
    if (result.status === 'denied') {
      opts.onError?.(
        'Clipboard access blocked. Click in the message, use Ctrl+Shift+V, or allow clipboard permission for this site.'
      );
      return;
    }
    if (result.status === 'empty') {
      setEditDropdownOpen(false);
      ReactEditor.focus(editor as ReactEditor);
      return;
    }
    const plain = result.plain ?? '';
    if (!plain) {
      setEditDropdownOpen(false);
      ReactEditor.focus(editor as ReactEditor);
      return;
    }
    const toInsert = [{ type: 'paragraph' as const, children: [{ text: plain }] }];
    if (saved) safeRestoreSelection(editor, saved);
    Transforms.insertFragment(editor, toInsert);
    setEditDropdownOpen(false);
    ReactEditor.focus(editor as ReactEditor);
  }, [editor, opts]);

  const Separator = () => <span className={cn('mx-0.5 w-px self-stretch min-h-[16px]', c.border)} style={{ backgroundColor: c.border }} aria-hidden />;

  return (
    <div
      className={cn('flex flex-wrap items-center gap-0.5 border-b p-1.5', c.border, className)}
      style={{ minHeight: 34 }}
      onMouseDown={handleToolbarMouseDown}
    >
      {opts.unlockQuotedBelow?.visible && (
        <>
          <ToolbarButton
            onMouseDown={(e) => {
              e.preventDefault();
              opts.unlockQuotedBelow?.onUnlock();
            }}
            title={
              opts.unlockQuotedBelow.title ??
              'Edit message below signature (quoted original and From/Sent header)'
            }
            ariaLabel={opts.unlockQuotedBelow.ariaLabel ?? 'Edit message below signature'}
            className={cn('min-w-[28px] min-h-[28px] p-1.5', c.text)}
          >
            <span className="flex items-center justify-center text-current">{ToolbarIcons.unlockOpen}</span>
          </ToolbarButton>
          <Separator />
        </>
      )}

      {/* Undo / Redo */}
      {HistoryEditor.isHistoryEditor(editor) && (
        <>
          <ToolbarButton
            onMouseDown={(e) => { e.preventDefault(); HistoryEditor.undo(editor); }}
            title="Undo (Ctrl+Z)"
            ariaLabel="Undo"
            className={cn('min-w-[28px] min-h-[28px] p-1.5', c.text)}
          >
            <span className="flex items-center justify-center text-current">{ToolbarIcons.undo}</span>
          </ToolbarButton>
          <ToolbarButton
            onMouseDown={(e) => { e.preventDefault(); HistoryEditor.redo(editor); }}
            title="Redo (Ctrl+Y)"
            ariaLabel="Redo"
            className={cn('min-w-[28px] min-h-[28px] p-1.5', c.text)}
          >
            <span className="flex items-center justify-center text-current">{ToolbarIcons.redo}</span>
          </ToolbarButton>
          <Separator />
        </>
      )}

      {/* Edit (Cut, Copy, Paste) dropdown */}
      <div className="relative inline-block">
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            saveSelectionForEdit();
            setEditDropdownOpen((open) => !open);
          }}
          className={cn(
            'inline-flex items-center justify-center gap-1 rounded-md min-w-[28px] min-h-[28px] px-1.5 py-1.5 text-xs hover:bg-black/5 transition-colors',
            c.text
          )}
          title="Edit"
        >
          <span className="flex items-center justify-center text-current">{ToolbarIcons.copy}</span>
          <span className="flex items-center justify-center text-current -ml-0.5">{ToolbarIcons.chevronDown}</span>
        </button>
        {editDropdownOpen && (
          <>
            <div
              className="fixed inset-0 z-10"
              aria-hidden
              onMouseDown={(e) => { e.preventDefault(); setEditDropdownOpen(false); }}
            />
            <div
              className={cn(
                'absolute left-0 top-full z-20 mt-1 min-w-[160px] rounded-md border bg-white p-1.5 shadow-lg dark:bg-gray-800',
                c.border
              )}
              onMouseDown={(e) => e.preventDefault()}
            >
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleCut();
                }}
                className={cn(
                  'w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-xs text-left transition-colors hover:bg-black/5',
                  c.text
                )}
                title="Cut (Ctrl+X)"
              >
                <span className="flex items-center justify-center text-current flex-shrink-0">{ToolbarIcons.cut}</span>
                <span>Cut</span>
              </button>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleCopy();
                }}
                className={cn(
                  'w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-xs text-left transition-colors hover:bg-black/5',
                  c.text
                )}
                title="Copy (Ctrl+C)"
              >
                <span className="flex items-center justify-center text-current flex-shrink-0">{ToolbarIcons.copy}</span>
                <span>Copy</span>
              </button>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  void handlePaste();
                }}
                className={cn(
                  'w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-xs text-left transition-colors hover:bg-black/5',
                  c.text
                )}
                title="Paste (Ctrl+V)"
              >
                <span className="flex items-center justify-center text-current flex-shrink-0">{ToolbarIcons.paste}</span>
                <span>Paste</span>
              </button>
              <div className={cn('my-1 border-t', c.border)} />
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  void handlePasteWithoutFormatting();
                }}
                className={cn(
                  'w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-xs text-left transition-colors hover:bg-black/5',
                  c.text
                )}
                title="Paste without formatting (Ctrl+Shift+V)"
              >
                <span className="flex items-center justify-center text-current flex-shrink-0">{ToolbarIcons.paste}</span>
                <span>Paste without formatting</span>
              </button>
            </div>
          </>
        )}
      </div>
      <Separator />

      {/* Paragraph style (Thunderbird-compatible) */}
      {opts.showHeadings && (
        <>
          <select
            className={cn('text-xs rounded border bg-transparent px-1.5 py-1 min-h-[28px]', c.border, c.text)}
            value={getActiveParagraphStyle(editor)}
            onChange={(e) => {
              const v = e.target.value as ParagraphStyleValue;
              const blockType = PARAGRAPH_STYLE_TO_BLOCK[v] ?? 'paragraph';
              toggleBlock(editor, blockType);
            }}
            title="Paragraph style"
            aria-label="Paragraph style"
          >
            <option value="body">Body Text</option>
            <option value="p">Paragraph</option>
            <option value="h1">Heading 1</option>
            <option value="h2">Heading 2</option>
            <option value="h3">Heading 3</option>
            <option value="h4">Heading 4</option>
            <option value="h5">Heading 5</option>
            <option value="h6">Heading 6</option>
            <option value="address">Address</option>
            <option value="pre">Preformat</option>
          </select>
          <Separator />
        </>
      )}

      {/* Font family, color, size */}
      {opts.showFont && (
        <>
          <select
            className={cn('max-w-[160px] text-xs rounded border bg-transparent px-1.5 py-1 min-h-[28px]', c.border, c.text)}
            value={currentFontFamily}
            onChange={(e) => {
              const next = e.target.value;
              if (next === FONT_MENU_SEPARATOR) return;
              runToolbarMutation(editor, onContentChange, () => setMarkValue(editor, 'fontFamily', next));
            }}
            title="Font family"
            aria-label="Font family"
          >
            {fontSections.map((section, sectionIndex) => {
              if (section.kind === 'separator') {
                return (
                  <option key={`sep-${sectionIndex}`} value={`${FONT_MENU_SEPARATOR}-${sectionIndex}`} disabled>
                    {FONT_MENU_SEPARATOR}
                  </option>
                );
              }
              return section.options.map((font) => (
                <option key={`${font.value || 'variable'}-${font.label}`} value={font.value}>
                  {font.label}
                </option>
              ));
            })}
            {showCustomFontOption ? (
              <option value={currentFontFamily}>{currentFontFamily.replace(/^["']|["']$/g, '')}</option>
            ) : null}
          </select>
          {opts.showColors && (
            <>
              <ToolbarButton
                active={hasHighlightAtSelection(editor)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  saveSelectionForColorPicker();
                  runToolbarMutation(editor, onContentChange, () => {
                    const saved = colorPickerSelectionRef.current;
                    if (saved) safeRestoreSelection(editor, saved);
                    applyHighlight(editor, DEFAULT_HIGHLIGHT_COLOR);
                    ReactEditor.focus(editor as ReactEditor);
                  });
                }}
                title="Highlight"
                aria-label="Highlight"
                className={cn('min-w-[28px] min-h-[28px] p-1.5 rounded-md border', c.border, c.text)}
              >
                <span className="flex items-center justify-center text-current">{ToolbarIcons.highlight}</span>
              </ToolbarButton>
              <input
                ref={colorInputRef}
                type="color"
                aria-label="Font color"
                value={hexForColorInput(getMarkValueAtSelection(editor, 'color'), '#000000')}
                onMouseDown={handleColorInputMouseDown}
                onChange={(e) => {
                  const newColor = e.target.value;
                  const saved = colorPickerSelectionRef.current;
                  colorPickerSelectionRef.current = null;
                  runToolbarMutation(editor, onContentChange, () => {
                    if (saved) safeRestoreSelection(editor, saved);
                    setMarkValue(editor, 'color', newColor);
                    ReactEditor.focus(editor as ReactEditor);
                  });
                }}
                className={cn('w-7 h-7 cursor-pointer rounded-md border p-0.5 min-w-[28px] min-h-[28px]', c.border)}
                style={{ borderColor: c.border }}
                title="Font color"
              />
              <input
                ref={highlightInputRef}
                type="color"
                aria-label="Highlight color"
                value={hexForColorInput(getMarkValueAtSelection(editor, 'backgroundColor'), DEFAULT_HIGHLIGHT_COLOR)}
                onMouseDown={handleHighlightInputMouseDown}
                onChange={(e) => {
                  const newColor = e.target.value;
                  const saved = colorPickerSelectionRef.current;
                  colorPickerSelectionRef.current = null;
                  runToolbarMutation(editor, onContentChange, () => {
                    if (saved) safeRestoreSelection(editor, saved);
                    setMarkValue(editor, 'backgroundColor', newColor);
                    ReactEditor.focus(editor as ReactEditor);
                  });
                }}
                className={cn('w-7 h-7 cursor-pointer rounded-md border p-0.5 min-w-[28px] min-h-[28px]', c.border)}
                style={{ borderColor: c.border }}
                title="Highlight color"
              />
            </>
          )}
          <ToolbarButton
            onMouseDown={(e) => { e.preventDefault(); decreaseFontSize(editor, onContentChange); }}
            title="Decrease font size"
            className={cn('min-w-[28px] min-h-[28px] p-1.5 rounded-md border bg-gray-50 dark:bg-gray-800/50', c.border, c.text)}
          >
            <span className="flex items-center justify-center text-current">{ToolbarIcons.fontSizeDown}</span>
          </ToolbarButton>
          <ToolbarButton
            onMouseDown={(e) => { e.preventDefault(); increaseFontSize(editor, onContentChange); }}
            title="Increase font size"
            className={cn('min-w-[28px] min-h-[28px] p-1.5 rounded-md border bg-gray-50 dark:bg-gray-800/50', c.border, c.text)}
          >
            <span className="flex items-center justify-center text-current">{ToolbarIcons.fontSizeUp}</span>
          </ToolbarButton>
          <select
            className={cn('w-16 text-xs rounded border bg-transparent px-1.5 py-1 min-h-[28px]', c.border, c.text)}
            value={currentFontSizePx(editor)}
            onChange={(e) => runToolbarMutation(editor, onContentChange, () => setMarkValue(editor, 'fontSize', `${e.target.value}px`))}
            title="Font size"
          >
            {FONT_SIZES_PX.map((px) => (
              <option key={px} value={px}>
                {px}
              </option>
            ))}
          </select>
          <Separator />
        </>
      )}

      {/* Text style */}
      <ToolbarButton active={isMarkActive(editor, 'bold')} onToggle={() => runToolbarMutation(editor, onContentChange, () => toggleMark(editor, 'bold'))} title="Bold">
        <span className="flex items-center justify-center text-current">{ToolbarIcons.bold}</span>
      </ToolbarButton>
      <ToolbarButton active={isMarkActive(editor, 'italic')} onToggle={() => runToolbarMutation(editor, onContentChange, () => toggleMark(editor, 'italic'))} title="Italic">
        <span className="flex items-center justify-center text-current">{ToolbarIcons.italic}</span>
      </ToolbarButton>
      <ToolbarButton active={isMarkActive(editor, 'underline')} onToggle={() => runToolbarMutation(editor, onContentChange, () => toggleMark(editor, 'underline'))} title="Underline">
        <span className="flex items-center justify-center text-current">{ToolbarIcons.underline}</span>
      </ToolbarButton>
      <ToolbarButton active={isMarkActive(editor, 'strikethrough')} onToggle={() => runToolbarMutation(editor, onContentChange, () => toggleMark(editor, 'strikethrough'))} title="Strikethrough">
        <span className="flex items-center justify-center text-current">{ToolbarIcons.strikethrough}</span>
      </ToolbarButton>
      <Separator />

      {/* List dropdown */}
      {opts.showLists && (
        <>
          <div className="relative inline-block">
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                setListDropdownOpen((open) => !open);
              }}
              className={cn(
                'inline-flex items-center justify-center gap-1 rounded-md min-w-[28px] min-h-[28px] px-1.5 py-1.5 text-xs hover:bg-black/5 transition-colors',
                (isBlockActive(editor, 'bulleted-list') || isBlockActive(editor, 'numbered-list')) && 'bg-black/10 dark:bg-gray-600/30',
                c.text
              )}
              title="List"
            >
              <span className="flex items-center justify-center text-current">
                {isBlockActive(editor, 'numbered-list') ? ToolbarIcons.listOrdered : ToolbarIcons.list}
              </span>
              <span className="flex items-center justify-center text-current -ml-0.5">{ToolbarIcons.chevronDown}</span>
            </button>
            {listDropdownOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  aria-hidden
                  onMouseDown={(e) => { e.preventDefault(); setListDropdownOpen(false); }}
                />
                <div
                  className={cn(
                    'absolute left-0 top-full z-20 mt-1 min-w-[160px] rounded-md border bg-white p-1.5 shadow-lg dark:bg-gray-800',
                    c.border
                  )}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      toggleBlock(editor, 'bulleted-list');
                      setListDropdownOpen(false);
                    }}
                    className={cn(
                      'w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-xs text-left transition-colors hover:bg-black/5',
                      isBlockActive(editor, 'bulleted-list') && 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200',
                      c.text
                    )}
                    title="Bullet list"
                  >
                    <span className="flex items-center justify-center text-current flex-shrink-0">{ToolbarIcons.list}</span>
                    <span>Bullet list</span>
                  </button>
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      toggleBlock(editor, 'numbered-list');
                      setListDropdownOpen(false);
                    }}
                    className={cn(
                      'w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-xs text-left transition-colors hover:bg-black/5',
                      isBlockActive(editor, 'numbered-list') && 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200',
                      c.text
                    )}
                    title="Numbered list"
                  >
                    <span className="flex items-center justify-center text-current flex-shrink-0">{ToolbarIcons.listOrdered}</span>
                    <span>Numbered list</span>
                  </button>
                </div>
              </>
            )}
          </div>
          <Separator />
        </>
      )}

      {/* Alignment */}
      {opts.showAlignment && (
        <>
          <div className="relative inline-block">
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                setAlignDropdownOpen((open) => !open);
              }}
              className={cn(
                'inline-flex items-center justify-center gap-1 rounded-md min-w-[28px] min-h-[28px] px-1.5 py-1.5 text-xs hover:bg-black/5 transition-colors',
                c.text
              )}
              title="Alignment"
            >
              <span className="flex items-center justify-center text-current">
                {isAlignActive(editor, 'center')
                  ? ToolbarIcons.alignCenter
                  : isAlignActive(editor, 'right')
                    ? ToolbarIcons.alignRight
                    : isAlignActive(editor, 'justify')
                      ? ToolbarIcons.alignJustify
                      : ToolbarIcons.alignLeft}
              </span>
              <span className="flex items-center justify-center text-current -ml-0.5">{ToolbarIcons.chevronDown}</span>
            </button>
            {alignDropdownOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  aria-hidden
                  onMouseDown={(e) => { e.preventDefault(); setAlignDropdownOpen(false); }}
                />
                <div
                  className={cn(
                    'absolute left-0 top-full z-20 mt-1 min-w-[140px] rounded-md border bg-white p-1.5 shadow-lg dark:bg-gray-800',
                    c.border
                  )}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  {[
                    { value: 'left' as const, icon: ToolbarIcons.alignLeft, label: 'Align left' },
                    { value: 'center' as const, icon: ToolbarIcons.alignCenter, label: 'Center' },
                    { value: 'right' as const, icon: ToolbarIcons.alignRight, label: 'Align right' },
                    { value: 'justify' as const, icon: ToolbarIcons.alignJustify, label: 'Justify' },
                  ].map(({ value, icon, label }) => (
                    <button
                      key={value}
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setAlign(editor, value);
                        setAlignDropdownOpen(false);
                      }}
                      className={cn(
                        'w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-xs text-left transition-colors hover:bg-black/5',
                        isAlignActive(editor, value) && 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200',
                        c.text
                      )}
                      title={label}
                    >
                      <span className="flex items-center justify-center text-current flex-shrink-0">{icon}</span>
                      <span>{label}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          <Separator />
        </>
      )}

      {/* Insert (link + image) dropdown */}
      {(opts.showLink || opts.showImage) && (
        <>
          <div className="relative inline-block" data-toolbar-insert-ui>
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                setInsertDropdownOpen((open) => !open);
              }}
              className={cn(
                'inline-flex items-center justify-center gap-1 rounded-md min-w-[28px] min-h-[28px] px-1.5 py-1.5 text-xs hover:bg-black/5 transition-colors',
                isLinkActive(editor) && 'bg-black/10 dark:bg-gray-600/30',
                c.text
              )}
              title="Insert link or image"
              aria-label="Insert link or image"
            >
              <span className="flex items-center justify-center text-current">{ToolbarIcons.link}</span>
              <span className="flex items-center justify-center text-current -ml-0.5">{ToolbarIcons.chevronDown}</span>
            </button>
            {insertDropdownOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  aria-hidden
                  onMouseDown={(e) => { e.preventDefault(); setInsertDropdownOpen(false); }}
                />
                <div
                  className={cn(
                    'absolute left-0 top-full z-20 mt-1 min-w-[180px] rounded-md border bg-white p-1.5 shadow-lg dark:bg-gray-800',
                    c.border
                  )}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  {opts.showLink && (
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setInsertDropdownOpen(false);
                        handleLink();
                      }}
                      className={cn(
                        'w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-xs text-left transition-colors hover:bg-black/5',
                        isLinkActive(editor) && 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200',
                        c.text
                      )}
                      title="Insert link"
                    >
                      <span className="flex items-center justify-center text-current flex-shrink-0">{ToolbarIcons.link}</span>
                      <span>Insert link</span>
                    </button>
                  )}
                  {opts.showImage && (
                    <>
                      {opts.showLink && <div className={cn('my-1 border-t', c.border)} />}
                      {onInsertImage && (
                        <button
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setInsertDropdownOpen(false);
                            onInsertImage();
                          }}
                          className={cn(
                            'w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-xs text-left transition-colors hover:bg-black/5',
                            c.text
                          )}
                          title="Insert image from URL"
                        >
                          <span className="flex items-center justify-center text-current flex-shrink-0">{ToolbarIcons.image}</span>
                          <span>Image from URL</span>
                        </button>
                      )}
                      <button
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          const sel = editor.selection;
                          imageSelectionRef.current = sel && isRangeSafeForSelect(editor, sel) ? {
                            anchor: { path: [...sel.anchor.path], offset: sel.anchor.offset },
                            focus: { path: [...sel.focus.path], offset: sel.focus.offset },
                          } : null;
                          setInsertDropdownOpen(false);
                          imageFileInputRef.current?.click();
                        }}
                        className={cn(
                          'w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-xs text-left transition-colors hover:bg-black/5',
                          c.text
                        )}
                        title="Embed image from file"
                      >
                        <span className="flex items-center justify-center text-current flex-shrink-0">{ToolbarIcons.image}</span>
                        <span>Embed image from file</span>
                      </button>
                    </>
                  )}
                </div>
              </>
            )}
            <input
              ref={imageFileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (!file || !file.type.startsWith('image/')) return;
                const reader = new FileReader();
                reader.onload = () => {
                  const dataUrl = reader.result as string;
                  imageSelectionRef.current = null;
                  insertEmailImage(editor, dataUrl);
                  ReactEditor.focus(editor as ReactEditor);
                  queueMicrotask(() => onContentChange?.());
                };
                reader.readAsDataURL(file);
              }}
            />
          </div>
          {linkOpen && (
            <span data-toolbar-link-ui className="inline-flex items-center gap-1.5">
              <input
                type="url"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://..."
                className={cn('w-40 rounded border px-2 py-1 text-xs min-h-[28px]', c.border)}
                onKeyDown={(e) => e.key === 'Enter' && applyLink()}
              />
              <button type="button" onMouseDown={(e) => { e.preventDefault(); applyLink(); }} className={cn('rounded px-2 py-1 text-xs min-h-[28px] hover:bg-black/5', c.text)}>
                Apply
              </button>
            </span>
          )}
          <Separator />
        </>
      )}

      {/* Table */}
      {opts.showTable && (
        <>
          <div className="relative inline-block">
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                setTableDropdownOpen((open) => !open);
              }}
              className={cn(
                'inline-flex items-center justify-center gap-1 rounded-md min-w-[28px] min-h-[28px] px-1.5 py-1.5 text-xs hover:bg-black/5 transition-colors',
                c.text
              )}
              title="Table"
            >
              <span className="flex items-center justify-center text-current">{ToolbarIcons.table}</span>
              <span className="flex items-center justify-center text-current -ml-0.5">{ToolbarIcons.chevronDown}</span>
            </button>
            {tableDropdownOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  aria-hidden
                  onMouseDown={(e) => { e.preventDefault(); setTableDropdownOpen(false); }}
                />
                <div
                  className={cn(
                    'absolute left-0 top-full z-20 mt-1 min-w-[200px] rounded-md border bg-white p-2.5 shadow-lg dark:bg-gray-800',
                    c.border
                  )}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <div className="mb-2 text-sm font-medium text-gray-500 dark:text-gray-400">
                    Insert table
                  </div>
                  <div className="grid grid-cols-4 gap-1.5 mb-3">
                    {TABLE_SIZE_OPTIONS.map(({ rows, cols, label }) => (
                      <button
                        key={`${rows}-${cols}`}
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          insertTable(editor, rows, cols);
                          setTableDropdownOpen(false);
                        }}
                        className={cn(
                          'rounded-md px-2 py-1.5 text-xs transition-colors hover:bg-indigo-100 hover:text-indigo-800 dark:hover:bg-indigo-900/40 dark:hover:text-indigo-200',
                          c.text
                        )}
                        title={`Insert ${rows}×${cols} table`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <div className="border-t border-gray-200 dark:border-gray-600 pt-2 space-y-0.5">
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        if (canMergeTableColumns(editor)) {
                          mergeTableColumns(editor);
                          setTableDropdownOpen(false);
                        }
                      }}
                      disabled={!canMergeTableColumns(editor)}
                      className={cn(
                        'w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-xs text-left transition-colors hover:bg-black/5 disabled:opacity-50 disabled:cursor-not-allowed',
                        c.text
                      )}
                      title="Merge selected cells in the same row (select 2+ cells then click)"
                    >
                      <span className="flex items-center justify-center text-current flex-shrink-0">{ToolbarIcons.merge}</span>
                      <span>Merge cells</span>
                    </button>
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        if (getTableRowContext(editor)) {
                          insertTableRow(editor, 'above');
                          setTableDropdownOpen(false);
                        }
                      }}
                      disabled={!getTableRowContext(editor)}
                      className={cn(
                        'w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-xs text-left transition-colors hover:bg-black/5 disabled:opacity-50 disabled:cursor-not-allowed',
                        c.text
                      )}
                      title="Add row above"
                    >
                      <span className="flex items-center justify-center text-current flex-shrink-0">{ToolbarIcons.rowAbove}</span>
                      <span>Add row above</span>
                    </button>
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        if (getTableRowContext(editor)) {
                          insertTableRow(editor, 'below');
                          setTableDropdownOpen(false);
                        }
                      }}
                      disabled={!getTableRowContext(editor)}
                      className={cn(
                        'w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-xs text-left transition-colors hover:bg-black/5 disabled:opacity-50 disabled:cursor-not-allowed',
                        c.text
                      )}
                      title="Add row below"
                    >
                      <span className="flex items-center justify-center text-current flex-shrink-0">{ToolbarIcons.rowBelow}</span>
                      <span>Add row below</span>
                    </button>
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        if (getTableContext(editor)) {
                          equalizeTableColumnWidths(editor);
                          setTableDropdownOpen(false);
                        }
                      }}
                      disabled={!getTableContext(editor)}
                      className={cn(
                        'w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-xs text-left transition-colors hover:bg-black/5 disabled:opacity-50 disabled:cursor-not-allowed',
                        c.text
                      )}
                      title="Give every column the same width (percentage of the table)"
                    >
                      <span className="flex items-center justify-center text-current flex-shrink-0">{ToolbarIcons.columnsEqual}</span>
                      <span>Equal column widths</span>
                    </button>
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        if (!getTableContext(editor)) return;
                        const raw = typeof window !== 'undefined' ? window.prompt('Height for all rows (px)', '40') : null;
                        if (raw === null) return;
                        const n = parseInt(String(raw).replace(/\D/g, ''), 10);
                        if (!Number.isFinite(n) || n < 1) return;
                        applyUniformTableRowHeight(editor, n);
                        setTableDropdownOpen(false);
                      }}
                      disabled={!getTableContext(editor)}
                      className={cn(
                        'w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-xs text-left transition-colors hover:bg-black/5 disabled:opacity-50 disabled:cursor-not-allowed',
                        c.text
                      )}
                      title="Set the same height on every row in this table"
                    >
                      <span className="flex items-center justify-center text-current flex-shrink-0">{ToolbarIcons.rowsEqual}</span>
                      <span>Set all row heights…</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
          <Separator />
        </>
      )}

      {/* More */}
      <div className="relative inline-block">
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            setMoreDropdownOpen((open) => !open);
          }}
          className={cn(
            'inline-flex items-center justify-center gap-1 rounded-md min-w-[28px] min-h-[28px] px-1.5 py-1.5 text-xs hover:bg-black/5 transition-colors',
            c.text
          )}
          title="More options"
          aria-label="More formatting options"
        >
          <span className="flex items-center justify-center text-current">{ToolbarIcons.more}</span>
        </button>
        {moreDropdownOpen && (
          <>
            <div
              className="fixed inset-0 z-10"
              aria-hidden
              onMouseDown={(e) => { e.preventDefault(); setMoreDropdownOpen(false); }}
            />
            <div
              className={cn(
                'absolute right-0 top-full z-20 mt-1 min-w-[220px] rounded-md border bg-white p-1.5 shadow-lg dark:bg-gray-800',
                c.border
              )}
              onMouseDown={(e) => e.preventDefault()}
            >
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  runToolbarMutation(editor, onContentChange, () => clearFormat(editor));
                  setMoreDropdownOpen(false);
                }}
                className={cn(
                  'w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-xs text-left transition-colors hover:bg-black/5',
                  c.text
                )}
                title="Clear formatting"
              >
                <span className="flex items-center justify-center text-current flex-shrink-0">{ToolbarIcons.clearFormat}</span>
                <span>Clear formatting</span>
              </button>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  if (isLinkActive(editor)) {
                    unwrapLink(editor);
                    setMoreDropdownOpen(false);
                  }
                }}
                disabled={!isLinkActive(editor)}
                className={cn(
                  'w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-xs text-left transition-colors hover:bg-black/5 disabled:opacity-50 disabled:cursor-not-allowed',
                  c.text
                )}
                title="Remove link"
              >
                <span className="flex items-center justify-center text-current flex-shrink-0">{ToolbarIcons.link}</span>
                <span>Remove link</span>
              </button>
              <div className={cn('border-t border-gray-200 dark:border-gray-600 mt-1.5 pt-1.5 px-2.5 py-1.5 text-xs', c.textMuted)}>
                Paste without format: Ctrl+Shift+V
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

function ToolbarButton({
  active,
  onToggle,
  onMouseDown,
  disabled,
  className,
  title,
  ariaLabel,
  children,
}: {
  active?: boolean;
  onToggle?: () => void;
  onMouseDown?: (e: React.MouseEvent) => void;
  disabled?: boolean;
  className?: string;
  title: string;
  ariaLabel?: string;
  children: React.ReactNode;
}) {
  const handleMouseDown = (e: React.MouseEvent) => {
    if (onMouseDown) {
      onMouseDown(e);
    } else {
      e.preventDefault();
      onToggle?.();
    }
  };
  return (
    <button
      type="button"
      onMouseDown={handleMouseDown}
      disabled={disabled}
      title={title}
      aria-label={ariaLabel ?? title}
      className={cn(
        'inline-flex items-center justify-center rounded-md transition-colors hover:bg-black/5',
        active ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300' : '',
        disabled ? 'opacity-50 cursor-not-allowed' : '',
        className ?? 'min-w-[28px] min-h-[28px] p-1.5'
      )}
    >
      {children}
    </button>
  );
}

export default SlateEmailToolbar;
